// src/components/SOWBriefingPanel.jsx
// Drop into DraftTab just after the opening <div> return statement (before library status callout)
// Import: import SOWBriefingPanel from '../SOWBriefingPanel';
// Usage:  <SOWBriefingPanel contract={contract} updateC={updateC} apiKey={apiKey} callAI={callAI} toast={toast} />

import { useState } from 'react';
import supabase from '../utils/supabaseClient';

export default function SOWBriefingPanel({ contract, updateC, apiKey, callAI, toast }) {
  const saved     = contract.pennyDrafts?.sowBriefing || null;
  const savedRef  = contract.pennyDrafts?.sowRefNum   || '';

  const [refInput, setRefInput]   = useState(savedRef);
  const [loading, setLoading]     = useState(false);
  const [briefing, setBriefing]   = useState(saved);
  const [pennyBrief, setPennyBrief] = useState(contract.pennyDrafts?.pennyBriefText || '');
  const [briefLoading, setBriefLoading] = useState(false);
  const [expanded, setExpanded]   = useState(!!saved);
  const [error, setError]         = useState('');

  // ── Load briefing from Supabase ──────────────────────────────────
  async function loadBriefing() {
    const ref = refInput.trim().toUpperCase();
    if (!ref.startsWith('GS-SOW-')) {
      setError('Reference number must start with GS-SOW- (e.g. GS-SOW-2025-CALTRANS-0042)');
      return;
    }
    setLoading(true); setError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('get_sow_briefing', { ref_num: ref });
      if (rpcError) throw rpcError;
      if (!data) { setError('No SOW draft found with reference number: ' + ref); setLoading(false); return; }

      setBriefing(data);
      setExpanded(true);

      // Persist to contract so it survives sessions
      updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), sowBriefing: data, sowRefNum: ref } });
      toast.success('SOW briefing loaded — ' + ref);

      // Auto-generate Penny briefing summary
      await generatePennyBrief(data);
    } catch (e) {
      setError('Failed to load briefing: ' + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ── Penny synthesizes CM history into analyst briefing ───────────
  async function generatePennyBrief(b) {
    if (!apiKey) return;
    setBriefLoading(true);
    try {
      const messages = (b.messages || []).filter(m => m.role === 'user').map(m => m.content).join('\n');
      const painPoints = (b.pain_points || []).join(', ') || 'none noted';
      const complianceNotes = (b.compliance_notes || []);
      const citations = (b.scm_citations || []).map(c => c.citation || c).join(', ');

      const system = `You are Penny briefing a contract analyst. A Contract Manager previously used GoldenScope to draft a Statement of Work.
Summarize the CM's SOW session into a concise analyst briefing. Be direct and actionable. Structure:

1. CM INTENT (2-3 sentences) — What the CM was trying to accomplish in plain language.
2. KEY ANSWERS — The Five W's the CM provided (who, what, where, when, why, how) in bullet form.
3. BUDGET — State the CM's estimated budget and note what solicitation type this likely supports based on California SCM thresholds (e.g., under $5K = direct award, under $50K = informal competitive, over $50K = formal).
4. AREAS OF UNCERTAINTY — Where the CM struggled, was vague, or needed clarification. Be specific.
5. COMPLIANCE FLAGS — What Penny flagged during review, if anything. Cite SCM sections.
6. SUPPORTING DOCUMENTS — List any documents the CM uploaded and note which are tagged for contract inclusion.
7. ANALYST RECOMMENDATIONS — 2-3 specific things the analyst should probe deeper on or verify before drafting begins.

Keep the total briefing under 400 words. Be specific — not generic.`;

      const supportingDocsSummary = (b.supporting_docs || []).map(d =>
        d.name + ' [' + (d.tag || 'Other') + ']' + (d.includeInContract ? ' — tagged for contract inclusion' : ' — reference only')
      ).join('; ') || 'none uploaded';

      const userMsg = `SOW Reference: ${b.reference_number}
CM: ${b.cm_profile?.name || 'Unknown'} · ${b.cm_profile?.agency_name || ''}
Initial idea: ${b.initial_idea || 'Not recorded'}
Estimated budget: ${b.estimated_budget || 'Not provided'}
Five W answers: ${JSON.stringify(b.five_ws || {})}
CM conversation (user messages only): ${messages.slice(0, 2000)}
Pain points noted: ${painPoints}
Compliance flags: ${complianceNotes.map(n => n.issue + ' (' + n.scmRef + ')').join('; ') || 'none'}
SCM citations used: ${citations || 'none'}
Supporting documents: ${supportingDocsSummary}`;

      const text = await callAI(apiKey, {
        system, messages: [{ role: 'user', content: userMsg }], max_tokens: 600,
      });
      setPennyBrief(text);
      updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), pennyBriefText: text } });
    } catch (e) {
      console.error('Penny brief error:', e);
    } finally {
      setBriefLoading(false);
    }
  }

  function clearBriefing() {
    setBriefing(null); setPennyBrief(''); setRefInput(''); setExpanded(false); setError('');
    updateC({ pennyDrafts: { ...(contract.pennyDrafts || {}), sowBriefing: null, sowRefNum: '', pennyBriefText: '' } });
    toast.info('SOW briefing cleared');
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <div style={{
      marginBottom: 16, borderRadius: 'var(--r-md)',
      border: briefing ? '1.5px solid var(--gold-300, #fbbf24)' : '1.5px solid var(--slate-200)',
      background: briefing ? 'var(--gold-50, #fffbeb)' : 'var(--slate-50)',
      overflow: 'hidden',
    }}>

      {/* Header */}
      <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: briefing ? 'pointer' : 'default' }}
        onClick={() => briefing && setExpanded(e => !e)}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg,#c49922,#e8c04a)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, fontWeight: 900, color: '#0f1f3d',
        }}>P</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900, #0f1f3d)' }}>
            {briefing ? '✅ SOW Briefing Loaded — ' + briefing.reference_number : '📋 Link CM-Drafted SOW'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--slate-500)', marginTop: 1 }}>
            {briefing
              ? `CM: ${briefing.cm_profile?.name || 'Unknown'} · Click to ${expanded ? 'collapse' : 'expand'} briefing`
              : 'If the CM submitted a GoldenScope SOW reference number, enter it here — Penny will brief you on the full CM session before you begin drafting.'}
          </div>
        </div>
        {briefing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={e => { e.stopPropagation(); generatePennyBrief(briefing); }}
              disabled={briefLoading}
              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--gold-300,#fbbf24)', background: '#fff', color: '#92400e', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              {briefLoading ? '…' : '↺ Re-brief'}
            </button>
            <button onClick={e => { e.stopPropagation(); clearBriefing(); }}
              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--red-200,#fecaca)', background: '#fff', color: 'var(--red-500,#ef4444)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              ✕ Clear
            </button>
          </div>
        )}
      </div>

      {/* Input row — shown when no briefing loaded */}
      {!briefing && (
        <div style={{ padding: '0 14px 12px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            value={refInput}
            onChange={e => setRefInput(e.target.value.toUpperCase())}
            onKeyDown={e => { if (e.key === 'Enter' && refInput.trim()) loadBriefing(); }}
            placeholder="GS-SOW-2025-AGENCY-0042"
            style={{
              flex: 1, padding: '8px 12px', borderRadius: 7,
              border: '1.5px solid var(--slate-200)', fontSize: 13,
              fontFamily: 'monospace', outline: 'none',
            }}
          />
          <button
            onClick={loadBriefing}
            disabled={!refInput.trim() || loading}
            style={{
              padding: '8px 16px', borderRadius: 7, border: 'none',
              background: !refInput.trim() ? 'var(--slate-200)' : 'linear-gradient(135deg,#c49922,#e8c04a)',
              color: !refInput.trim() ? 'var(--slate-400)' : '#0f1f3d',
              fontWeight: 700, fontSize: 13, cursor: !refInput.trim() ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}>
            {loading ? 'Loading…' : '✦ Load Briefing'}
          </button>
        </div>
      )}

      {error && (
        <div style={{ margin: '0 14px 12px', padding: '8px 12px', borderRadius: 7, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Expanded briefing content */}
      {briefing && expanded && (
        <div style={{ borderTop: '1px solid var(--gold-200,#fde68a)' }}>

          {/* Penny briefing summary */}
          <div style={{ padding: '14px 16px', background: '#0f1f3d' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c49922', letterSpacing: '.06em', marginBottom: 8 }}>
              ✒ PENNY'S ANALYST BRIEFING
            </div>
            {briefLoading ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,.3)', animation: `pulse 1.2s ease-in-out ${i*0.2}s infinite` }} />)}
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', marginLeft: 6 }}>Penny is reading the CM session…</span>
              </div>
            ) : pennyBrief ? (
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,.85)', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                {pennyBrief}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', fontStyle: 'italic' }}>
                Briefing not yet generated — click Re-brief above.
              </div>
            )}
          </div>

          {/* Raw SOW metadata */}
          <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>

            {/* CM Profile */}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid var(--gold-200,#fde68a)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: '.06em', marginBottom: 6 }}>CONTRACT MANAGER</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f1f3d' }}>{briefing.cm_profile?.name || 'Unknown'}</div>
              {briefing.cm_profile?.title    && <div style={{ fontSize: 12, color: '#64748b' }}>{briefing.cm_profile.title}</div>}
              {briefing.cm_profile?.agency_name && <div style={{ fontSize: 12, color: '#64748b' }}>{briefing.cm_profile.agency_name}</div>}
              {briefing.cm_profile?.email    && <div style={{ fontSize: 11, color: '#94a3b8' }}>{briefing.cm_profile.email}</div>}
              {briefing.cm_profile?.phone    && <div style={{ fontSize: 11, color: '#94a3b8' }}>{briefing.cm_profile.phone}</div>}
            </div>

            {/* SOW Stats */}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid var(--gold-200,#fde68a)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: '.06em', marginBottom: 6 }}>SOW DETAILS</div>
              <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.8 }}>
                <div><strong>Ref:</strong> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{briefing.reference_number}</span></div>
                <div><strong>Status:</strong> {briefing.status?.charAt(0).toUpperCase() + briefing.status?.slice(1)}</div>
                <div><strong>Messages:</strong> {(briefing.messages || []).length} exchanges</div>
                <div><strong>Compliance flags:</strong> {(briefing.compliance_notes || []).length}</div>
                <div><strong>SCM citations:</strong> {(briefing.scm_citations || []).length}</div>
              </div>
            </div>

            {/* Five W's quick view */}
            {briefing.five_ws && Object.keys(briefing.five_ws).length > 0 && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid var(--gold-200,#fde68a)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: '.06em', marginBottom: 8 }}>FIVE W's — CM ANSWERS</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {['who','what','where','when','why','how'].map(w => briefing.five_ws[w] && (
                    <div key={w} style={{ padding: '7px 10px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '.06em', marginBottom: 3 }}>
                        {w === 'who' ? '👤 WHO' : w === 'what' ? '📋 WHAT' : w === 'where' ? '📍 WHERE' : w === 'when' ? '📅 WHEN' : w === 'why' ? '💡 WHY' : '⚙️ HOW'}
                      </div>
                      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
                        {briefing.five_ws[w].slice(0, 120)}{briefing.five_ws[w].length > 120 ? '…' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Budget estimate */}
            {briefing.estimated_budget && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#92400e', letterSpacing: '.06em', marginBottom: 6 }}>💰 ESTIMATED BUDGET</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#0f1f3d' }}>{briefing.estimated_budget}</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>Internal only — not included in solicitation documents</div>
              </div>
            )}

            {/* Supporting documents */}
            {(briefing.supporting_docs || []).length > 0 && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid #e0f2fe' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#0284c7', letterSpacing: '.06em', marginBottom: 8 }}>
                  📎 SUPPORTING DOCUMENTS ({briefing.supporting_docs.length})
                </div>
                {briefing.supporting_docs.map((doc, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 5 }}>
                    <span style={{ fontSize: 14 }}>📄</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{doc.name}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>{doc.tag || 'Other'}</span>
                        {doc.includeInContract
                          ? <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>Tagged for contract</span>
                          : <span style={{ fontSize: 9, color: '#94a3b8' }}>Reference only</span>
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Compliance flags */}
            {(briefing.compliance_notes || []).length > 0 && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#d97706', letterSpacing: '.06em', marginBottom: 8 }}>🔍 COMPLIANCE FLAGS FROM CM SESSION</div>
                {briefing.compliance_notes.map((note, i) => (
                  <div key={i} style={{ padding: '6px 10px', borderRadius: 6, marginBottom: 6, background: note.severity === 'major' ? '#fef2f2' : '#fffbeb', border: '1px solid ' + (note.severity === 'major' ? '#fecaca' : '#fde68a') }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: note.severity === 'major' ? '#dc2626' : '#d97706' }}>
                      {note.severity === 'major' ? '⚠ Major' : '• Minor'} — {(note.section || '').toUpperCase()}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{note.issue}</div>
                    {note.recommendation && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>→ {note.recommendation}</div>}
                    {note.scmRef && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>📖 {note.scmRef}</div>}
                  </div>
                ))}
              </div>
            )}

            {/* SCM citations used */}
            {(briefing.scm_citations || []).length > 0 && (
              <div style={{ gridColumn: '1 / -1', padding: '10px 12px', borderRadius: 8, background: '#fff', border: '1px solid #e0f2fe' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#0284c7', letterSpacing: '.06em', marginBottom: 6 }}>📖 SCM REFERENCES FROM CM SESSION</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {[...new Set((briefing.scm_citations || []).map(c => c.citation || c))].map((cit, i) => (
                    <span key={i} style={{ padding: '3px 8px', borderRadius: 99, background: '#e0f2fe', border: '1px solid #bae6fd', color: '#0369a1', fontSize: 11, fontWeight: 500 }}>
                      {cit}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Analyst note */}
          <div style={{ padding: '8px 16px 12px', fontSize: 11.5, color: '#64748b', fontStyle: 'italic', borderTop: '1px solid var(--gold-100,#fef9c3)' }}>
            ℹ️ All analyst workflow steps remain unchanged — Penny is simply better prepared. Proceed through every step as normal.
          </div>
        </div>
      )}

      <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>
    </div>
  );
}
